import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Stack,
    Skeleton,
    Text,
    Flex,
    useStyleConfig,
    useBreakpointValue,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerBody,
    DrawerCloseButton,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {AdyenCheckout, Card, Dropin} from '@adyen/adyen-web'
import {FormattedMessage, useIntl, defineMessages} from 'react-intl'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {getCreditCardIcon, buildMaskedCardDisplay} from '../../utils/cc-utils'

/* Hooks */
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCustomToast} from '../../contexts/custom-toast-context'
import {VisaIcon, MastercardIcon} from '@salesforce/retail-react-app/app/components/icons'
import {InfoCircleBlack, AmexPaymentIcon} from '../../components/icons'
import {useBrand} from '../../contexts/brand-context'
import {useLegalPagePaths} from '../../hooks/use-legal-page-paths'

// MRT multi site
import {getSiteEnvVarValue} from '../../utils/site-config'
import {getAdyenLocale} from '../../integrations/adyen/utils/adyen-locale'

/* Components */
/* Styling */
import adyen_css from './styles/adyen.css'

/* Icons */
import { InfoCard } from '../../components/info-card'

/* Constants */

const messages = defineMessages({
    cardErrorGarage: {
        id: 'account_detail.payment.card_error.garage',
        defaultMessage: 'There’s an error with the credit card. Please review and try again.'
    },
    cardErrorDynamite: {
        id: 'account_detail.payment.card_error.dyanmite',
        defaultMessage: 'There’s an error with the credit card. Please review and try again.'
    },
    errorRemovingCard: {
        id: 'account_detail.payment.error_removing_card',
        defaultMessage: 'There’s an error on removing the credit card.'
    },
    creditCardAdded: {
        id: 'account_detail.payment.credit_card_added',
        defaultMessage: 'Credit card added'
    },
    creditCardRemoved: {
        id: 'account_detail.payment.card_removed',
        defaultMessage: 'Credit card removed'
    },
    isDefault: {
        id: 'account_detail.payment.default',
        defaultMessage: 'Default'
    },
    maxCardsReachedGarage: {
        id: 'account_detail.payment.max_saved_cards.garage',
        defaultMessage: 'Limit of 10 saved cards reached. Remove one to save a new card.'
    },
    maxCardsReachedDynamite: {
        id: 'account_detail.payment.max_saved_cards.dynamite',
        defaultMessage: 'Limit of 10 saved cards reached. Remove one to save a new card.'
    },

    // Adyen card-field validation. The wording is ours rather than Adyen's, so it has to
    // be declared here instead of inherited from Adyen's own translation bundle.
    adyenHolderNameRequiredGarage: {
        id: 'required_field.label.garage',
        defaultMessage: 'Please fill out this field'
    },
    adyenHolderNameRequiredDynamite: {
        id: 'required_field.label.dynamite',
        defaultMessage: 'Please fill out this field'
    },
    adyenCardNumberAsShownGarage: {
        id: 'adyen.error.card_number_as_shown.garage',
        defaultMessage: 'Please enter the number as it appears on your card'
    },
    adyenCardNumberAsShownDynamite: {
        id: 'adyen.error.card_number_as_shown.dynamite',
        defaultMessage: 'Please enter the number as it appears on your card'
    },
    adyenCardNumberInvalid: {
        id: 'adyen.error.card_number_invalid',
        defaultMessage: 'Invalid Credit Card Number'
    },
    adyenCardExpired: {
        id: 'adyen.error.card_expired',
        defaultMessage: 'This Credit Card has expired'
    },
    adyenExpiryMonthRequiredGarage: {
        id: 'adyen.error.expiry_month_required.garage',
        defaultMessage: 'Please select an Expiration Month'
    },
    adyenExpiryMonthRequiredDynamite: {
        id: 'adyen.error.expiry_month_required.dynamite',
        defaultMessage: 'Please select an Expiration Month'
    },
    adyenExpiryDateRequiredGarage: {
        id: 'adyen.error.expiry_date_required.garage',
        defaultMessage: 'Enter an expiration date'
    },
    adyenExpiryDateRequiredDynamite: {
        id: 'adyen.error.expiry_date_required.dynamite',
        defaultMessage: 'Enter an expiration date'
    },
    adyenExpiryDateInvalidGarage: {
        id: 'adyen.error.expiry_date_invalid.garage',
        defaultMessage: 'Enter a valid expiry date'
    },
    adyenExpiryDateInvalidDynamite: {
        id: 'adyen.error.expiry_date_invalid.dynamite',
        defaultMessage: 'Enter a valid expiry date'
    },
    adyenSecurityCodeRequiredGarage: {
        id: 'adyen.error.security_code_required.garage',
        defaultMessage: 'Please enter the security code'
    },
    adyenSecurityCodeRequiredDynamite: {
        id: 'adyen.error.security_code_required.dynamite',
        defaultMessage: 'Please enter the security code'
    },
    adyenSecurityCodeInvalid: {
        id: 'adyen.error.security_code_invalid',
        defaultMessage: 'Invalid Security Code'
    }
})

/**
 * SavedPaymentMethod is a React functional component that displays a user's saved payment method details,
 * such as card type, last four digits, expiration date, and default status. It provides UI controls for
 * removing the payment method, including a confirmation modal dialog. The component supports responsive
 * design and uses custom hooks for toast notifications and internationalization. It also handles the
 * deletion of a payment instrument via a mutation and refreshes the payment methods list upon successful
 * removal. The component expects properties for payment details, customer identification, and a callback
 * to fetch updated payment methods.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.expirationMonth - The expiration month of the payment method.
 * @param {string} props.expirationYear - The expiration year of the payment method.
 * @param {string} props.id - The unique identifier for the payment method.
 * @param {string} props.type - The type of payment method (e.g., credit card type).
 * @param {string} props.lastFour - The last four digits of the payment method.
 * @param {boolean} props.isDefault - Indicates if this is the default payment method.
 * @param {string} props.customerId - The unique identifier for the customer.
 * @param {function} props.fetchPaymentMethods - Callback to refresh the list of payment methods.
 *
 * @returns {JSX.Element} The rendered payment method card with remove functionality.
 */
const SavedPaymentMethod = ({
    expirationMonth,
    expirationYear,
    id,
    type,
    lastFour,
    isDefault,
    customerId,
    fetchPaymentMethods,
    index
}) => {
    const {showCustomToast} = useCustomToast()
    const intl = useIntl()
    const deleteCustomerPaymentInstrument = useShopperCustomersMutation(
        'deleteCustomerPaymentInstrument'
    )
    const CardTypeToIcon = getCreditCardIcon(type)
    const removeCard = async (id) => {
        try {
            await deleteCustomerPaymentInstrument.mutateAsync({
                parameters: {
                    customerId,
                    paymentInstrumentId: id
                }
            })

            fetchPaymentMethods()
            showCustomToast({
                message: intl.formatMessage(messages.creditCardRemoved)
            })
        } catch {
            showCustomToast({
                message: intl.formatMessage(messages.errorRemovingCard)
            })
        }
    }
    return (
        <InfoCard
            id={id}
            title=""
            isEditable={false}
            isDeletable={true}
            onDelete={() => removeCard(id)}
            vStackSpacing={2}
            hStackAlign="center"
            badge={
                isDefault ? (
                    <Text
                        as="span"
                        fontSize={{base: '11px', lg: '12px'}}
                        textTransform="uppercase"
                        lineHeight="18px"
                        fontWeight="500"
                        border="1px solid black"
                        width="fit-content"
                        padding="0 4px"
                    >
                        <FormattedMessage
                            defaultMessage="Default Credit Card"
                            id="account_detail.default.credit_card"
                        />
                    </Text>
                ) : null
            }
            summary={
                <Box display="flex" alignItems="center" gap="8px">
                    <CardTypeToIcon width="36px" height="36px" />

                    <Text as="span" fontSize={{base: '12px', lg: '14px'}} fontWeight={'400'} data-private>
                        {buildMaskedCardDisplay(lastFour, 4, '•')}
                    </Text>
                </Box>
            }
        />
    )
}

SavedPaymentMethod.propTypes = {
    expirationMonth: PropTypes.string,
    expirationYear: PropTypes.string,
    id: PropTypes.string,
    type: PropTypes.string,
    lastFour: PropTypes.string,
    isDefault: PropTypes.bool,
    customerId: PropTypes.string,
    fetchPaymentMethods: PropTypes.func,
    index: PropTypes.number,
}

/**
 * Renders the fixed page heading for saved payment methods.
 * Add card uses the drawer title inside AddPaymentMethod.
 */
const HeadingPayment = ({headingRef}) => (
    <Text
        alignItems="center"
        as="h1"
        textStyle="webH1"
        tabIndex="0"
        ref={headingRef}
        gap={6}
    >
        <FormattedMessage
            defaultMessage="Payment Options"
            id="account_detail.title.saved_payment"
        />
    </Text>
)

HeadingPayment.propTypes = {
    headingRef: PropTypes.shape({current: PropTypes.any})
}

/**
 * AddPaymentMethod is a React functional component that provides a user interface for adding a new credit card payment method using Adyen's Drop-in integration.
 *
 * This component handles the entire payment method addition flow, including device fingerprinting, Adyen Drop-in initialization, form validation, error handling, and submission of payment details to the backend. It supports both desktop and mobile layouts, and provides feedback to the user in case of errors or validation issues.
 *
 * Key Features:
 * - Integrates with Adyen's Drop-in UI for secure credit card entry and 3DS authentication.
 * - Dynamically loads Adyen's device fingerprinting script and manages its lifecycle.
 * - Validates cardholder input fields and enables/disables the submission button accordingly.
 * - Handles payment submission, additional details (e.g., 3DS), and error states.
 * - Supports localization and multi-site configurations.
 * - Accepts payment methods data and a callback for when payment is completed.
 *
 * Props:
 * @param {Object} paymentMethods - The available payment methods response from Adyen, used to configure the Drop-in component.
 * @param {Function} onPaymentCompleted - Callback function invoked when the payment method has been successfully added.
 *
 * Usage:
 * <AddPaymentMethod paymentMethods={paymentMethods} onPaymentCompleted={handleCompleted} />
 *
 * Dependencies:
 * - AdyenCheckout, Dropin, Card (Adyen Web SDK)
 * - Chakra UI components and hooks
 * - React Intl for localization
 * - Custom hooks: useBreakpointValue, useStyleConfig, useAccessToken, useMultiSite
 * - PropTypes for prop validation
 */
const AddPaymentMethod = ({paymentMethods, onPaymentCompleted}) => {
    const isMobile = useBreakpointValue({base: true, lg: false})
    const styles = useStyleConfig('CustomAccountAdyenPayments')
    const intl = useIntl()
    const {isGarage} = useBrand()
    const {privacyPolicyPath, termsAndConditionsPath} = useLegalPagePaths()
    const cardError = isGarage ? messages.cardErrorGarage : messages.cardErrorDynamite
    const {getTokenWhenReady} = useAccessToken()
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const parameters = {
        ...appConfig.commerceAPI.parameters
    }
    const proxyPath = appConfig.commerceAPI.proxyPath
    const organizationId = parameters.organizationId
    const siteId = parameters.siteId
    const [fingerprintScriptLoaded, setFingerprintScriptLoaded] = useState(false)
    const [isDropinLoaded, setIsDropinLoaded] = useState(false)
    const [dropinConfigsReady, setDropinConfigsReady] = useState(false)
    const {locale} = useMultiSite()
    const paymentContainerRef = useRef(null)
    const dropinRef = useRef(null)
    const [errorMessage, setErrorMessage] = useState(null)
    const [isAddCardDisabled, setIsAddCardDisabled] = useState(true)
    const [notAdditionalDetailsView, setNotAdditionalDetailsView] = useState(true)
    const [fieldValidity, setFieldValidity] = useState({
        holderName: false,
        encryptedCardNumber: false,
        encryptedExpiryMonth: false,
        encryptedExpiryYear: false,
        encryptedSecurityCode: false
    })
    const [setAsDefault, setSetAsDefault] = useState(false)
    const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream

    const onChange = (data, _component) => {
        const {valid} = data
        const cardType = data?.data?.paymentMethod?.brand

        // Update the security code label based on the card type
        if (_component?.dropinRef?.base?.querySelector('[data-id="encryptedSecurityCode"]')) {
            switch (cardType) {
                case 'amex':
                    _component.dropinRef.base.querySelector('[data-id="encryptedSecurityCode"]').textContent = 'CVV'
                    break;
                default:
                    _component.dropinRef.base.querySelector('[data-id="encryptedSecurityCode"]').textContent = 'CVV'
                    break;
            }
        }

        // Logic to disable the submit button based on field validation
        const relevantFields = {
            holderName: valid.holderName,
            encryptedCardNumber: valid.encryptedCardNumber,
            encryptedExpiryMonth: valid.encryptedExpiryMonth,
            encryptedExpiryYear: valid.encryptedExpiryYear,
            encryptedSecurityCode: valid.encryptedSecurityCode
        }

        setFieldValidity((prevState) => {
            const newState = {
                ...prevState,
                ...relevantFields
            }
            const allFieldsValid = Object.values(newState).every((isValid) => isValid)

            setIsAddCardDisabled(!allFieldsValid)

            return newState
        })
    }

    const ADYEN_CLIENT_KEY = getSiteEnvVarValue(appConfig, 'ADYEN_CLIENT_KEY', siteId)
    const ADYEN_MODE = getSiteEnvVarValue(appConfig, 'ADYEN_MODE', siteId)

    const adyenLocale = getAdyenLocale(locale.id)

    const checkoutConfiguration = {
        clientKey: ADYEN_CLIENT_KEY,
        environment: ADYEN_MODE,
        locale: adyenLocale,
        countryCode: locale.id.split('-')[1],
        cspNonce: Date.now().toString(),
        amount: {value: 0, currency: locale.preferredCurrency},
        paymentMethodsResponse: paymentMethods?.paymentMethods,
        translations: {
            [adyenLocale]: {
                /* Labels */
                'creditCard.holderName': intl.formatMessage({
                    id: 'adyen.label.cardholder_name',
                    defaultMessage: 'Cardholder name'
                }),
                'creditCard.cardNumber.label': intl.formatMessage({
                    id: 'adyen.label.card_number',
                    defaultMessage: 'Card Number'
                }),
                'creditCard.expiryDate.label': intl.formatMessage({
                    id: 'adyen.label.expiry_date',
                    defaultMessage: 'Expiry date'
                }),
                'creditCard.securityCode.label': intl.formatMessage({
                    id: 'adyen.label.cvv',
                    defaultMessage: 'CVV'
                }),

                /* Errors */
                // Holder name errors
                'creditCard.holderName.invalid': intl.formatMessage(
                    isGarage
                        ? messages.adyenHolderNameRequiredGarage
                        : messages.adyenHolderNameRequiredDynamite
                ),

                // Credit card number errors
                'creditCard.numberField.invalid': intl.formatMessage(
                    isGarage
                        ? messages.adyenCardNumberAsShownGarage
                        : messages.adyenCardNumberAsShownDynamite
                ),
                'cc.num.900': intl.formatMessage(
                    isGarage
                        ? messages.adyenCardNumberAsShownGarage
                        : messages.adyenCardNumberAsShownDynamite
                ),
                'cc.num.901': intl.formatMessage(messages.adyenCardNumberInvalid),

                // Expire date errors
                'cc.dat.912': intl.formatMessage(messages.adyenCardExpired),
                'cc.dat.913': intl.formatMessage(messages.adyenCardExpired),
                'cc.mth.915': intl.formatMessage(
                    isGarage
                        ? messages.adyenExpiryMonthRequiredGarage
                        : messages.adyenExpiryMonthRequiredDynamite
                ),
                'cc.yr.917': intl.formatMessage(
                    isGarage
                        ? messages.adyenExpiryDateRequiredGarage
                        : messages.adyenExpiryDateRequiredDynamite
                ),
                'cc.dat.910': intl.formatMessage(
                    isGarage
                        ? messages.adyenExpiryDateInvalidGarage
                        : messages.adyenExpiryDateInvalidDynamite
                ),

                // Security code
                'cc.cvc.920': intl.formatMessage(
                    isGarage
                        ? messages.adyenSecurityCodeRequiredGarage
                        : messages.adyenSecurityCodeRequiredDynamite
                ),
                'cc.cvc.921': intl.formatMessage(messages.adyenSecurityCodeInvalid),
            }
        },
        onSubmit: async (state, _component, actions) => {
            try {
                const token = await getTokenWhenReady()
                const body = JSON.stringify({
                    orderId: 'recurringPayment-account',
                    adyenData: state,
                    deviceFingerprint: document.getElementById('adyen-device-fingerprint').value,
                    locale,
                    returnUrl: `${window.location.origin}/account/payment`
                })

                const paymentResult = await fetch(
                    `${proxyPath}/custom/adyen-payments/v1/organizations/${organizationId}/adyen-payments?siteId=${siteId}&c_brand=Garage`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: body
                    }
                )

                const result = await paymentResult.json()
                if (
                    !result.resultCode ||
                    result.resultCode === 'Error' ||
                    (result.additionalData &&
                        result.additionalData['tokenization.store.operationType'] ===
                            'alreadyExisting')
                ) {
                    actions.reject()
                    return
                }

                if (result?.action?.type === 'threeDS2') {
                    setNotAdditionalDetailsView(false)
                }

                actions.resolve(result)
            } catch (error) {
                console.error(
                    '[payment-details - AddPaymentMethod - checkoutConfiguration] onSubmit',
                    error
                )
                actions.reject()
            }
        },
        onAdditionalDetails: async (state, component, actions) => {
            try {
                let result = {}
                const token = await getTokenWhenReady()
                const additionalDetailsResult = await fetch(
                    `${proxyPath}/custom/adyen-payments-details/v1/organizations/${organizationId}/adyen-payments-details?siteId=${siteId}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify(state.data)
                    }
                )

                result = await additionalDetailsResult.json()

                if (!result.adyenData && !result.adyenData.resultCode) {
                    actions.reject()
                    return
                }

                const {resultCode} = result.adyenData

                if (resultCode === 'Refused') {
                    setNotAdditionalDetailsView(true)
                }

                actions.resolve({
                    resultCode
                })
            } catch (error) {
                console.error('onSubmit', error)
                actions.reject()
            }
        },
        onPaymentCompleted: () => {
            onPaymentCompleted()
        },

        onPaymentFailed: async () => {
            setErrorMessage(intl.formatMessage(cardError))
            refreshDropin()
        },

        onError: () => {
            setErrorMessage(intl.formatMessage(cardError))
            refreshDropin()
        }
    }

    let [paymentMethodsConfiguration, setPaymentMethodsConfiguration] = useState({
        card: {
            enableStoreDetails: false,
            showStoredPaymentMethods: false,
            positionHolderNameOnTop: true,
            showPayButton: false,
            showBrandIcon: true,
            hasHolderName: true,
            holderNameRequired: true,
            name: 'Credit Card',
            amount: {value: 0, currency: locale.preferredCurrency},
            styles: {
                base: {
                    padding: '22px 8px 4px 0',
                    fontSize: iOS ? '16px' : '14px',
                    lineHeight: '20px',
                    fontWeight: 'normal',
                    color: 'black'
                },
                placeholder: {
                    fontSize: iOS ? '16px' : '14px',
                    fontWeight: '400',
                    lineHeight: '20px',
                    color: 'black'
                }
            },
            placeholders: {
                cardNumber: '',
                expiryDate: 'MM/YY',
                securityCodeThreeDigits: '',
                securityCodeFourDigits: '',
                holderName: ' '
            },
            onLoad: () => {
                setIsDropinLoaded(true)
                setIsAddCardDisabled(!dropinRef?.current?.isValid)
            },
            onChange: onChange
        }
    })

    const refreshDropin = async () => {
        try {
            // Unmount existing dropin
            if (dropinRef.current) {
                dropinRef.current.unmount()
            }

            // Clear the container
            if (paymentContainerRef.current) {
                paymentContainerRef.current.innerHTML = ''
            }

            // Reset states
            setIsDropinLoaded(false)
            setIsAddCardDisabled(true)
            setFingerprintScriptLoaded(false)

            // Recreate dropin
            if (paymentMethods && paymentContainerRef?.current) {
                const checkout = await AdyenCheckout({...checkoutConfiguration, paymentMethods})
                const dropin = new Dropin(checkout, {
                    paymentMethodComponents: [Card],
                    paymentMethodsConfiguration: paymentMethodsConfiguration
                })?.mount(paymentContainerRef?.current)
                dropinRef.current = dropin
            }
        } catch (error) {
            console.error('Error refreshing dropin:', error)
        }
    }

    useEffect(() => {
        setDropinConfigsReady(true)
    }, [])

    useEffect(() => {
        try {
            const createCheckout = async () => {
                if (paymentMethods && paymentContainerRef?.current && dropinConfigsReady) {
                    const checkout = await AdyenCheckout({...checkoutConfiguration, paymentMethods})
                    const dropin = new Dropin(checkout, {
                        paymentMethodComponents: [Card],
                        paymentMethodsConfiguration: paymentMethodsConfiguration
                    })?.mount(paymentContainerRef?.current)
                    dropinRef.current = dropin
                }
            }
            createCheckout()
        } catch (error) {
            console.error('Error creating dropin:', error)
        }
    }, [paymentMethods, dropinConfigsReady])

    useEffect(() => {
        if (!fingerprintScriptLoaded && document.getElementById('adyen-device-fingerprint')) {
            document.getElementById('adyen-device-fingerprint').remove()
        }

        if (!document.getElementById('adyen-device-fingerprint')) {
            const input = document.createElement('input')
            input.type = 'hidden'
            input.id = 'adyen-device-fingerprint'
            input.name = 'adyen-device-fingerprint'
            document.body.appendChild(input)
        }

        if (!fingerprintScriptLoaded) {
            const script = document.createElement('script')
            const now = new Date()
            const yyyy = now.getFullYear()
            const mm = String(now.getMonth() + 1).padStart(2, '0')
            const dd = String(now.getDate()).padStart(2, '0')
            const version = `${yyyy}${mm}${dd}`
            script.src = `https://live.adyen.com/hpp/js/df.js?v=${version}`
            script.async = true
            script.nonce = window.__CSP_NONCE__
            script.onload = () => {
                setFingerprintScriptLoaded(true)
                window.dfSet('adyen-device-fingerprint')
            }
            document.body.appendChild(script)
        }
    }, [fingerprintScriptLoaded])

    const handleSubmitPayment = () => {
        const dropin = dropinRef?.current
        dropin.submit()
    }

    return (
        <Box
            key="adyen-card-container"
            id="card-container"
            css={[adyen_css]}
            sx={styles}
            maxW={{base: 'full', lg: '458px'}}
            margin={{base: '0 auto', lg: '0'}}
            display="flex"
            flexDirection="column"
            height="100%"
        >
            {/* CONTENT AREA */}
            <Box flex="1">
                <Box borderBottom={'1px solid #ECE9E9'} padding={'16px'}>
                    <Text
                        as="h2"
                        fontSize={{base: '16px', lg: '20px'}}
                        lineHeight={'20px'}
                        fontWeight="700"
                        textTransform="uppercase"
                    >
                        <FormattedMessage
                            defaultMessage="Add Credit Card"
                            id="account_detail.title.add_credit_card"
                        />
                    </Text>
                </Box>

                <Box px={'4'} py={'5'}>
                    <Flex align="center" justify="space-between" mb={3}>
                        {/* SUBTITLE */}
                        <Text
                            fontSize={{base: 'xs', lg: 'sm'}}
                            lineHeight={'20px'}
                            fontWeight="700"
                            textTransform="uppercase"
                        >
                            <FormattedMessage
                                defaultMessage="Card Information"
                                id="account_detail.subtitle.card_information"
                            />
                        </Text>

                        {/* CARD ICONS */}
                        <Flex direction="row" gap={'2px'} align="center">
                            <VisaIcon boxSize={8} />
                            <MastercardIcon boxSize={8} />
                            <AmexPaymentIcon boxSize={8} backgroundColor="white" textAlign="end" />
                        </Flex>
                    </Flex>
                    {errorMessage && !isMobile && (
                        <Text
                            paddingBottom={{base: 6}}
                            fontSize={{base: 'xs'}}
                            color={{base: 'dangerText'}}
                        >
                            {errorMessage}
                        </Text>
                    )}

                    <Box
                        className="payment floating-labels account-payment-options"
                        ref={paymentContainerRef}
                        sx={{
                            '.adyen-checkout__input-wrapper': {
                                border: '1px solid #939393 !important',
                                borderRadius: '0 !important'
                            },
                            '.adyen-checkout__label': {
                                left: '10px !important'
                            },
                            '.adyen-checkout__input': {
                                paddingLeft: '8px !important',
                                fontSize: iOS ? '16px !important' : '14px !important',
                                fontWeight: '400 !important',
                                lineHeight: '20px !important'
                            },
                            '.adyen-checkout__label__text::after': {
                                content: '" *"',
                                color: '#000',
                                fontWeight: 400,
                                fontSize: '14px'
                            },
                        }}
                    />

                    {errorMessage && isMobile && (
                        <Text fontSize={{base: 'xs'}} color={{base: 'dangerText'}}>
                            {errorMessage}
                        </Text>
                    )}

                    <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
                        {/* CVV NOTE */}
                        <Flex justifyContent={'flex-end'}>
                            <Text
                                fontSize="12px"
                                w={{base: '47%'}}
                                fontWeight={'400'}
                                lineHeight={{base: '16px', lg: '20px'}}
                                color="#585858"
                            >
                                <FormattedMessage
                                    defaultMessage="3–4 digit code found on the back or front of your card."
                                    id="account_detail.text.card_security_code_info"
                                />
                            </Text>
                        </Flex>

                        <Flex flexDirection={'column'} gap={'20px'}>
                            <Box display={'flex'} gap={'8px'} alignItems={'center'}>
                                <input
                                    data-testid="account-checkbox-set-default-credit-card"
                                    type="checkbox"
                                    width={'16px'}
                                    height={'16px'}
                                    checked={setAsDefault}
                                    onChange={(e) => setSetAsDefault(e.target.checked)}
                                />
                                <Text
                                    fontSize="12px"
                                    fontWeight={'400'}
                                    color={'black'}
                                    lineHeight={{base: '16px', lg: '20px'}}
                                >
                                    <FormattedMessage
                                        defaultMessage="Set as my Default Credit Card"
                                        id="account_detail.checkbox.default_credit_card"
                                    />
                                </Text>
                            </Box>

                            <Text
                                fontSize="12px"
                                fontWeight="400"
                                w={'98%'}
                                lineHeight={{base: '16px', lg: '20px'}}
                                color="#676767"
                            >
                                <FormattedMessage
                                    id="account_detail.text.recaptcha_privacy_terms"
                                    defaultMessage="This site is protected by reCAPTCHA and Google {privacyLink} and {termsLink} apply."
                                    values={{
                                        privacyLink: (
                                            <Link
                                                data-testid="account-link-privacy-policy"
                                                href={privacyPolicyPath}
                                                textDecoration="underline"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Privacy Policy"
                                                    id="account_detail.link.privacy_policy"
                                                />
                                            </Link>
                                        ),
                                        termsLink: (
                                            <Link
                                                data-testid="account-link-terms-of-service"
                                                href={termsAndConditionsPath}
                                                textDecoration="underline"
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Terms of Service"
                                                    id="account_detail.link.terms_of_services"
                                                />
                                            </Link>
                                        )
                                    }}
                                />
                            </Text>
                        </Flex>
                    </Box>
                </Box>
            </Box>

            {isDropinLoaded && notAdditionalDetailsView && (
                <Box borderTop={'1px solid #ECE9E9'} px={'4'} pt={'4'}>
                    <Button
                        data-testid="account-button-save-credit-card"
                        onClick={handleSubmitPayment}
                        fontSize={'14px'}
                        fontWeight={'700'}
                        lineHeight={{base: '16px', lg: '20px'}}
                        width={{base: '100%'}}
                        minH={{base: '48px'}}
                        isDisabled={isAddCardDisabled}
                        opacity="1 !important"
                    >
                        <FormattedMessage
                            defaultMessage="Save Credit Card"
                            id="account_detail.title.save_credit_card"
                        />
                    </Button>
                </Box>
            )}
        </Box>
    )
}

AddPaymentMethod.propTypes = {
    paymentMethods: PropTypes.object,
    onPaymentCompleted: PropTypes.func
}

/**
 * PaymentDetails is a React functional component that manages and displays the user's saved payment methods
 * within their account settings. It provides functionality to fetch, display, and add new credit cards,
 * leveraging Adyen's customer wallet API through a proxy. The component adapts its layout responsively
 * for mobile and desktop views, and uses various hooks for internationalization, authentication, and
 * toast notifications. It conditionally renders UI elements based on the presence of saved payment methods,
 * and provides user feedback upon successful addition of a new payment method. The component also handles
 * loading states and ensures the default payment method is prioritized in the display order.
 *
 * @component
 * @returns {JSX.Element} The rendered payment details section, including saved payment methods and the option to add new ones.
 */
const PaymentDetails = () => {
    const intl = useIntl()
    const isMobile = useBreakpointValue({base: true, lg: false})
    const {showCustomToast} = useCustomToast()
    const {data: customer} = useCurrentCustomer()
    const {getTokenWhenReady} = useAccessToken()
    const {app: appConfig} = getConfig()
    const {isGarage} = useBrand()

    const parameters = {
        ...appConfig.commerceAPI.parameters
    }
    const proxyPath = appConfig.commerceAPI.proxyPath
    const organizationId = parameters.organizationId
    const siteId = parameters.siteId
    const [paymentMethods, setPaymentMethods] = useState(null)
    const [showAddPayment, setShowAddPayment] = useState(false)
    const [isPMSLoading, setIsPMSLoading] = useState(false)
    const headingRef = useRef()

    // Maximum number of saved cards allowed - from ADYEN config group or default to 10
    const MAX_SAVED_CARDS = parseInt(getSiteEnvVarValue(appConfig, 'MAX_SAVED_CARDS_NUMBER', siteId)) || 10

    // Check if max saved cards limit has been reached
    const currentSavedCardsCount = paymentMethods?.cards?.length || 0
    const hasReachedMaxSavedCards = currentSavedCardsCount >= MAX_SAVED_CARDS
    const maxCardsReachedMessage = isGarage
        ? intl.formatMessage(messages.maxCardsReachedGarage)
        : intl.formatMessage(messages.maxCardsReachedDynamite)

    useEffect(() => {
        headingRef?.current?.focus()
    }, [])

    const fetchPaymentMethods = async () => {
        setIsPMSLoading(true)

        const token = await getTokenWhenReady()
        const response = await fetch(
            `${proxyPath}/custom/adyen-customer-wallet/v1/organizations/${organizationId}/customer-wallet?siteId=${siteId}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            }
        )
        const responseJson = await response.json()

        if (responseJson && responseJson.cards) {
            // Sort cards to have the default card first
            const sortedCards = responseJson.cards.sort((a, b) => b.default - a.default)
            setPaymentMethods({...responseJson, cards: sortedCards})
        }

        setIsPMSLoading(false)
    }

    useEffect(() => {
        fetchPaymentMethods()
    }, [])

    const handleAddCreditCard = () => {
        setShowAddPayment(true)
    }

    const handleOnPaymentCompleted = async () => {
        setShowAddPayment(false)
        showCustomToast({
            message: intl.formatMessage(messages.creditCardAdded)
        })
        await fetchPaymentMethods()
    }

    return (
        <Stack
            data-testid="account-payment"
            spacing={'6'}
            height={{base: '100%'}}
            maxW={{base: '458px'}}
        >
            {isHydrated() && (
                <>
                    <HeadingPayment key="account-payment-heading" ref={headingRef} />
                    <Box
                        data-testid="account-payment"
                        display={{base: 'flex'}}
                        justifyContent={{base: 'space-between'}}
                        flexDirection={{base: 'column'}}
                        flex={{base: '1'}}
                    >
                        <Skeleton
                            isLoaded={!isPMSLoading}
                            display={{base: 'flex'}}
                            justifyContent={{base: 'space-between'}}
                            flexDirection={{base: 'column'}}
                            flex={{base: '1'}}
                        >
                            {/* Add payment */}
                            <>
                                {paymentMethods?.cards?.length === 0 ? (
                                    /* No saved payments */
                                    <Box display={'flex'} flexDirection={'column'} gap={'12px'}>
                                        <Flex align="center" gap="6px">
                                            {/* BLACK DOT */}
                                            <InfoCircleBlack boxSize="16px" />
                                            <Text
                                                fontSize={{base: '12px', lg: '14px'}}
                                                lineHeight={{base: '18px', lg: '24px'}}
                                                fontWeight={{base: '500'}}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="No Saved Payment Options"
                                                    id="account_detail.subtitle.no_saved_payment"
                                                />
                                            </Text>
                                        </Flex>
                                        <Text
                                            fontSize={{base: '12px', lg: '14px'}}
                                            lineHeight={{base: '18px', lg: '24px'}}
                                            fontWeight={{base: '400'}}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Click below to save a payment method."
                                                id="account_detail.subtitle.add_payment_option"
                                            />
                                        </Text>
                                    </Box>
                                ) : (
                                    /* Saved payments */
                                    <Box display={'flex'} flexDirection={'column'} gap={'24px'}>
                                        {paymentMethods?.cards?.map((paymentMethod, index) => (
                                            <SavedPaymentMethod
                                                key={paymentMethod.id}
                                                {...paymentMethod}
                                                fetchPaymentMethods={fetchPaymentMethods}
                                                isDefault={paymentMethod.default}
                                                customerId={customer.customerId}
                                                index={index}
                                            />
                                        ))}
                                    </Box>
                                )}
                                {hasReachedMaxSavedCards && (
                                    <Box p={3} mb={3}>
                                        <Box
                                            p={3}
                                            borderRadius="4px"
                                            bg="webBasic.50"
                                            border="1px solid"
                                            borderColor="webBasic.300"
                                        >
                                            <Text fontSize={'14px'} color="webBasic.950" lineHeight={'20px'}>
                                                {maxCardsReachedMessage}
                                            </Text>
                                        </Box>
                                    </Box>
                                )}
                                <Button
                                    data-testid="account-button-add-credit-card"
                                    onClick={handleAddCreditCard}
                                    isDisabled={hasReachedMaxSavedCards}
                                    fontSize={'14px'}
                                    display={'flex'}
                                    justifyContent={'center'}
                                    alignItems={'center'}
                                    fontWeight={'700'}
                                    lineHeight={'20px'}
                                    marginTop={{
                                        base: paymentMethods?.cards?.length > 0 ? 6 : 12,
                                        lg: 6
                                    }}
                                    width={{base: 'full', lg: 'fit-content'}}
                                    minW={{lg: '225px'}}
                                    px={{lg: 6}}
                                    h={{base: '48px'}}
                                >
                                    <FormattedMessage
                                        defaultMessage="Add Credit Card"
                                        id="account_detail.title.add_credit_card"
                                    />
                                </Button>
                                <Drawer
                                    data-testid="account-drawer-add-payment"
                                    isOpen={showAddPayment}
                                    placement={isMobile ? 'bottom' : 'right'}
                                    onClose={() => setShowAddPayment(false)}
                                    size={isMobile ? 'full' : 'md'}
                                >
                                    <DrawerOverlay bg="rgba(0, 0, 0, 0.30)" />

                                    <DrawerContent
                                        marginTop={{
                                            base: 'auto',
                                            lg: '0'
                                        }}
                                        maxH={{
                                            base: '67vh',
                                            lg: '100vh'
                                        }}
                                        maxW={{
                                            base: '100%',
                                            lg: '458px'
                                        }}
                                        overflow="hidden"
                                        background="white"
                                    >
                                        <DrawerCloseButton
                                            data-testid="account-button-close-add-payment"
                                            mt={'18px'}
                                        />
                                        <DrawerBody
                                            sx={{
                                                paddingTop: '16px !important',
                                                paddingRight: '0 !important',
                                                paddingBottom: '16px !important',
                                                paddingLeft: '0 !important'
                                            }}
                                        >
                                            <AddPaymentMethod
                                                paymentMethods={paymentMethods}
                                                onPaymentCompleted={() => {
                                                    setShowAddPayment(false)
                                                    handleOnPaymentCompleted()
                                                }}
                                            />
                                        </DrawerBody>
                                    </DrawerContent>
                                </Drawer>
                            </>
                        </Skeleton>
                    </Box>
                </>
            )}
        </Stack>
    )
}

PaymentDetails.getTemplateName = () => 'account-payment'

export default PaymentDetails
